
import { useCallback, useContext, useMemo, useState } from "react";
import { GlobalContext } from "../../../Context/GlobalContext";

import useOrdersMain from "../../Services/OrdersMainServices/useOrdersMain";
import useOrders from "../../Services/OrderServices/useOrders";

export const useOrdersData = () => {
  const {
    api,
    AllClients = [],
    adminId,
    currentAdmin,
    latitude,
    longitude,
  } = useContext(GlobalContext);

  const {getOrderEvents: getOrderEventsApi}=useOrdersMain();
  const {getAllCatalogueOrders }=useOrders();
  
  const ORDER_LIMIT = 100;

  const [newOrderPage, setNewOrderPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);

  const [newOrderHasNext, setNewOrderHasNext] = useState(false);
  const [historyHasNext, setHistoryHasNext] = useState(false);

  const AdminPk = useMemo(() => {
    return adminId || currentAdmin?.pkid || currentAdmin?.adminId || "";
  }, [adminId, currentAdmin]);

  const [newOrders, setNewOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [loadingNew, setLoadingNew] = useState(false);
  const [loadingHis, setLoadingHis] = useState(false);


const getNewOrder = useCallback(
  async ({ client, from, to, page = newOrderPage }) => {
    if (!AdminPk) return;

    try {
      setLoadingNew(true);

      const result = await getAllCatalogueOrders({
        adminId: AdminPk,
        userid: client || "",
        statuses: [
          "received",
          "paid",
          "accepted",
          "prepared",
          "rejected",
          "refunded",
          "driver_assigned",
          "driver_confirmed",
          "driver_pickup_arrival",
          "delivery_picked",
          "driver_dropoff_arrival",
        ],
        fromdate: from,
        todate: to,
        latitude: latitude || "",
        longitude: longitude || "",
        limit: ORDER_LIMIT,
        offset: (page - 1) * ORDER_LIMIT,
      });

      if (result.success) {
        const rows = Array.isArray(result.data) ? result.data : [];

        if (rows.length === 0 && page > 1) {
          setNewOrderPage((prev) => Math.max(1, prev - 1));
          setNewOrderHasNext(false);
          return;
        }

        setNewOrders(rows);
        setNewOrderHasNext(rows.length === ORDER_LIMIT);
      } else {
        setNewOrders([]);
        setNewOrderHasNext(false);
      }
    } catch (error) {
      console.error("getNewOrder error:", error);
      setNewOrders([]);
      setNewOrderHasNext(false);
    } finally {
      setLoadingNew(false);
    }
  },
  [AdminPk, latitude, longitude, newOrderPage, getAllCatalogueOrders],
);

const getHistory = useCallback(
  async ({ client, from, to, page = historyPage }) => {
    if (!AdminPk) return;

    try {
      setLoadingHis(true);

      const result = await getAllCatalogueOrders({
        adminId: AdminPk,
        userid: client || "",
        statuses: [
          "completed",
          "dispatched",
          "delivered",
          "ready_for_dinning",
          "ready_for_takeaway",
          "not-delivered",
          "driver_assigned",
          "driver_confirmed",
          "driver_pickup_arrival",
          "delivery_picked",
          "driver_dropoff_arrival",
        ],
        fromdate: from,
        todate: to,
        latitude: latitude || "",
        longitude: longitude || "",
        limit: ORDER_LIMIT,
        offset: (page - 1) * ORDER_LIMIT,
      });

      if (result.success) {
        const rows = Array.isArray(result.data) ? result.data : [];

        if (rows.length === 0 && page > 1) {
          setHistoryPage((prev) => Math.max(1, prev - 1));
          setHistoryHasNext(false);
          return;
        }

        setHistoryOrders(rows);
        setHistoryHasNext(rows.length === ORDER_LIMIT);
      } else {
        setHistoryOrders([]);
        setHistoryHasNext(false);
      }
    } catch (error) {
      console.error("getHistory error:", error);
      setHistoryOrders([]);
      setHistoryHasNext(false);
    } finally {
      setLoadingHis(false);
    }
  },
  [AdminPk, latitude, longitude, historyPage, getAllCatalogueOrders],
);

  // add in useOrdersData.js
const getOrderEvents = useCallback(
  async (orderId) => {
    const events = await getOrderEventsApi(orderId);
    return events;
  },
  [getOrderEventsApi]
);


  const clientOptions = useMemo(
    () =>
      AllClients.map((c) => ({
        value: c.us_code,
        label: `${c.us_code} — ${
          c.organization_name || c.us_name || c.us_loginid || ""
        }`,
      })),
    [AllClients]
  );

 return {
   AdminPk,
   clientOptions,

   newOrders,
   historyOrders,
   loadingNew,
   loadingHis,

   newOrderPage,
   historyPage,
   newOrderHasNext,
   historyHasNext,
   orderLimit: ORDER_LIMIT,

   setNewOrderPage,
   setHistoryPage,

   getNewOrder,
   getHistory,
   getOrderEvents,
 };
};
